import React, { useState } from "react";
import {
  Box,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  ListSubheader,
  Typography,
  TextField,
  Divider,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import calendarIcon from "../../assets/images/calendar.png";

const HistoryFilterComponent = ({ setSelectedFilters, selectedFilters,
  toDate, setToDate, fromDate, setFromDate, setFilter, filter }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [open, setOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null); // To track which date (From/To) is being selected

  const handleDateChange = (newValue) => {
    if (selectedField === "from") {
      setFromDate(newValue);
      if (toDate && moment(newValue).isAfter(toDate)) {
        setToDate(null);
      }
    } else if (selectedField === "to") {
      setToDate(newValue);
    }
    setOpen(false); // Close the date picker after selecting a date
  };

  const handleFilterChange = (filterValue) => {
    setSelectedFilters((prevFilters) =>
      prevFilters.includes(filterValue)
        ? prevFilters.filter((item) => item !== filterValue)
        : [...prevFilters, filterValue]
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: isMobile ? "center" : "space-between",
        flexWrap: isMobile ? "wrap" : "nowrap",
        gap: isMobile ? 2 : 0,
        marginBottom: 2,
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
      >
        {/* Filter Dropdown with Grouped Options */}
        <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
          <FormControl
            sx={{
              maxWidth: isMobile ? "100%" : "200px",
              minWidth: "200px",
              mt: "5px",
            }}
          >
            <Select
              variant="standard"
              multiple
              value={selectedFilters}
              //onChange={handleDropdownChange}
              displayEmpty
              renderValue={() => (
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontFamily: "Open Sans",
                    fontSize: "16px",
                    lineHeight: "20px",
                  }}
                >
                  Filter
                </Typography>
              )} // Prevent selected values from displaying inside dropdown
              size="small"
              startAdornment={
                <FilterListIcon
                  sx={{ marginRight: 1, color: "#1460AA", pb: "5px" }}
                />
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    // marginTop: "10px",
                    // height: "350px",
                    overflowY: "auto",
                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: "inherit",
                    },
                  },
                },
              }}
              sx={{
                "& .MuiSelect-icon": {
                  color: "#000000",
                },
                "&.MuiInput-underline:before": {
                  borderBottom: "1px solid #C9CACC",
                },
              }}
            >
              {/* Group 1: Last Modified */}
              <ListSubheader
                sx={{
                  backgroundColor: "#F1F7FB",
                  position: "static",
                  mt: -1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontFamily: "Open Sans",
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#000000",
                  height: "56px",
                }}
              >
                <span>Status</span>
                <ExpandMore />
              </ListSubheader>
              <MenuItem sx={{ p: 0 }} value="Created">
                <Checkbox
                  sx={{ color: "#1460AA" }}
                  checked={selectedFilters.includes("Created")}
                  onChange={() => handleFilterChange("Created")}

                />
                <ListItemText primary="Created" />
              </MenuItem>
              <MenuItem sx={{ p: 0 }} value="Modified">
                <Checkbox
                  sx={{ color: "#1460AA" }}
                  checked={selectedFilters.includes("Modified")}
                  onChange={() => handleFilterChange("Modified")}
                />
                <ListItemText primary="Modified" />
              </MenuItem>
              <MenuItem sx={{ p: 0 }} value="Deleted">
                <Checkbox
                  sx={{ color: "#1460AA" }}
                  checked={selectedFilters.includes("Deleted")}
                  onChange={() => handleFilterChange("Deleted")}
                />
                <ListItemText primary="Deleted" />
              </MenuItem>

              {/* Group 2: Seller Type */}
              <ListSubheader
                sx={{
                  backgroundColor: "#F1F7FB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontFamily: "Open Sans",
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#000000",
                  height: "56px",
                }}
              >
                <span>Table</span>
                <ExpandMore />
              </ListSubheader>
              <MenuItem sx={{ p: 0 }} value="Payment Catalogue">
                <Checkbox
                  sx={{ color: "#1460AA" }}
                  checked={selectedFilters.includes("Payment Catalogue")}
                  onChange={() => handleFilterChange("Payment Catalogue")}
                />
                <ListItemText primary="Payment Catalogue" />
              </MenuItem>
              <MenuItem sx={{ p: 0 }} value="Form of Payment">
                <Checkbox
                  sx={{ color: "#1460AA" }}
                  checked={selectedFilters.includes("Form of Payment")}
                  onChange={() => handleFilterChange("Form of Payment")}
                />
                <ListItemText primary="Form of Payment" />
              </MenuItem>
              <MenuItem sx={{ p: 0 }} value="Point of Sale">
                <Checkbox
                  sx={{ color: "#1460AA" }}
                  checked={selectedFilters.includes("Point of Sale")}
                  onChange={() => handleFilterChange("Point of Sale")}
                />
                <ListItemText primary="Point of Sale" />
              </MenuItem>
            </Select>
          </FormControl>

          {/* Date filter */}
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {/* From | To Label with a single border bottom */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid #C9CACC", // Single border at the bottom
                  paddingBottom: 1, // Adds spacing between text and the border
                  minWidth: "240px",
                  mt: "-20px",
                }}
              >
                <img
                  src={calendarIcon}
                  alt="calendar"
                  style={{ paddingTop: "15px" }}
                />
                <Typography
                  onClick={() => {
                    setSelectedField("from");
                    setOpen(true);
                  }}
                  sx={{
                    cursor: "pointer",
                    minWidth: "90px",
                    fontFamily: "Open Sans",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#000000",
                    mt: "18px",
                    ml: "8px",
                    // paddingRight: 2, // Adds space between "From" and "|"
                  }}
                >
                  {fromDate ? moment(fromDate).format("YYYY/MM/DD") : "From"}
                </Typography>
                <Divider
                  orientation="vertical"
                  // flexItem
                  sx={{
                    margin: "0 8px",
                    mt: "10px",
                    height: "35px",
                    backgroundColor: "#C9CACC",
                  }}
                />

                <Typography
                  onClick={() => {
                    setSelectedField("to");
                    setOpen(true);
                  }}
                  sx={{
                    cursor: "pointer",
                    mr: "10px",
                    paddingLeft: 2, // Adds space between "|" and "To"
                    fontFamily: "Open Sans",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#000000",
                    minWidth: "80px",
                    mt: "16px",
                  }}
                >
                  {toDate ? moment(toDate).format("YYYY/MM/DD") : "To"}
                </Typography>
              </Box>

              {/* Date Picker (Conditionally rendered based on which field is selected) */}
              {open && (
                <DatePicker
                  value={selectedField === "from" ? fromDate : toDate}
                  onChange={handleDateChange}
                  open={open}
                  format={"YYYY/MM/DD"}
                  onClose={() => setOpen(false)}
                  shouldDisableDate={(date) =>
                    selectedField === "to" && fromDate && moment(date).isBefore(fromDate)
                  }
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      variant="outlined"
                      sx={{ display: "none" }}
                    />
                  )}
                />
              )}
            </Box>
          </LocalizationProvider>

          {/* FOP Search filter */}
          <TextField
            variant="standard"
            size="small"
            placeholder="Search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{
                    mr: 1,
                    width: "19.7px",
                    height: "19.7px",
                    color: "#1460AA",
                    pb: "5px",
                  }}
                />
              ),
              sx: {
                mt: "5px",
                "& input::placeholder": {
                  color: "#000000",
                  fontWeight: 600,
                  fontFamily: "Open Sans",
                  opacity: 1,
                },
                "&.MuiInput-underline:before": {
                  borderBottom: "1px solid #C9CACC",
                },
              },
            }}
          ></TextField>
        </Box>
      </Box>
    </Box>
  );
};

export default HistoryFilterComponent;
